import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';

const TERMINAL_CRM_RESYNC_STATUSES = new Set(['complete', 'failed']);

// Known worst case: 2 crm-connect sync heartbeats at ~2 minutes each. Used only to drive a
// determinate progress estimate — actual completion is signalled by useGetCrmResyncStatus
// polling, not this timer.
const CRM_RESYNC_EXPECTED_DURATION_MS = 4 * 60 * 1000;
const CRM_RESYNC_PROGRESS_TICK_MS = 1000;
const CRM_RESYNC_MAX_ESTIMATED_PERCENT = 95;

export type CrmResyncResult = {
  requestedAt: string;
  baselineSystemModstamp: string | null;
};

export type CrmResyncStatus = {
  status: 'complete' | 'failed' | 'pending';
  message?: string;
};

export function useCrmResyncOpportunity() {
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      const response = await OffersService.crmResyncOpportunity(opportunityId);
      return response.result as CrmResyncResult;
    },
  });
}

export function useGetCrmResyncStatus(
  opportunityId: string | undefined,
  requestedAt: string | undefined,
  { enabled = false }: { enabled?: boolean } = {},
) {
  return useQuery<CrmResyncStatus>({
    queryKey: offerQueryKeys.crmResyncStatus(opportunityId, requestedAt),
    queryFn: async () => {
      const response = await OffersService.getCrmResyncStatus(opportunityId, requestedAt);
      return response.result as CrmResyncStatus;
    },
    enabled: enabled && !!opportunityId && !!requestedAt,
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      if (query.state.error) {
        return false;
      }
      if (!status) {
        return 10000;
      }

      return TERMINAL_CRM_RESYNC_STATUSES.has(status) ? false : 10000;
    },
  });
}

// Renders a worst-case estimated progress bar to indicate progress — ticks while `isActive`,
// capped below 100% so it never visually completes ahead of the real status.
export function useCrmResyncProgressPercent(requestedAt: string | undefined, isActive: boolean) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const intervalId = setInterval(() => setNow(Date.now()), CRM_RESYNC_PROGRESS_TICK_MS);
    return () => clearInterval(intervalId);
  }, [isActive]);

  if (!requestedAt) {
    return 0;
  }

  return Math.min(
    CRM_RESYNC_MAX_ESTIMATED_PERCENT,
    ((now - new Date(requestedAt).getTime()) / CRM_RESYNC_EXPECTED_DURATION_MS) * 100,
  );
}
